import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  TextField,
  Typography,
  Alert,
  styled,
  FormControl,
  RadioGroup,
  Paper,
  FormControlLabel,
  Radio,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Divider,
} from "@mui/material";
import {
  CreditCard,
  BellRing,
  Lock,
  LogOut,
  Trash2,
  AlertCircle,
  Sparkles,
  Brain,
  CloudLightning,
  Check,
  Palette,
  ArrowRight,
} from "lucide-react";
import CommonLayout from "../../components/CommonLayout";
import SettingsService from "../../services/Settings";
import SessionService from "../../services/Session";
import { useDispatch } from "react-redux";
import { setUser } from "../../redux/actions/user";
import { logIssueNetworkRequestError } from "../../utils/Loggers";
import SubscriptionService from "../../services/Subscription";
import { capitalize } from "../../utils/String";
import { homeRoutePath } from "../Router";
import { useNavigate } from "react-router-dom";
import { config } from "../../config";
import { displayToastMessage } from "../../utils/Toasts";

// Custom styled components with updated design
const StyledCard = styled(motion.div)(({ theme }) => ({
  borderRadius: "12px",
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: "white",
  overflow: "hidden",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
  },
  marginBottom: "24px",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  borderRadius: "10px",
  padding: "10px 20px",
  fontWeight: 600,
  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.08)",
  transition: "all 0.2s ease",
  "&.MuiButton-containedPrimary": {
    backgroundColor: "#EF4444",
    color: "white",
    "&:hover": {
      backgroundColor: "#DC2626",
      transform: "translateY(-2px)",
    },
  },
  "&.MuiButton-outlined": {
    borderColor: theme.palette.grey[300],
    color: theme.palette.grey[800],
    "&:hover": {
      borderColor: theme.palette.grey[400],
      backgroundColor: theme.palette.grey[50],
      transform: "translateY(-2px)",
    },
  },
  "&.MuiButton-outlinedError": {
    borderColor: "#EF4444",
    color: "#EF4444",
    "&:hover": {
      backgroundColor: "#FEE2E2",
      borderColor: "#DC2626",
      transform: "translateY(-2px)",
    },
  },
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#EF4444",
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.08)",
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#EF4444",
  },
}));

// Animation variants with smoother transitions
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const modelOptions = [
  {
    id: "openai",
    name: "OpenAI",
    description:
      "Industry-leading versatility for complex marketing scenarios and dynamic creative generation. Included in all plans at no additional cost.",
    icon: Sparkles,
  },
  {
    id: "anthropic",
    name: "Anthropic",
    description:
      "Advanced reasoning and strategic ad targeting for high-stakes campaigns. Ideal for enterprises requiring real-time performance analysis and collaborative artifact generation.",
    icon: Brain,
  },
  {
    id: "deepseek",
    name: "DeepSeek",
    description:
      "Efficient processing of vast campaign datasets with 5x cost savings. Enables precise audience segmentation and advanced performance forecasting for data-driven decision-making.",
    icon: CloudLightning,
  },
  {
    id: "gemini",
    name: "Gemini",
    description:
      "Multimodal capabilities for cross-channel campaign optimization and visual storytelling. Enhances ad performance through unified processing of text, image, and video content.",
    icon: Palette,
  },
];

const pricingPlans = [
  {
    name: "Starter",
    price: "$49.99",
    features: [
      "All platforms",
      "0-$50k monthly ad spend",
      "Basic customer support",
      "Up to 6 projects created",
      "Up to 6 ICPs generated monthly",
      "Up to 4 ad strategies generated monthly",
      "Up to 4 campaign insights reports monthly",
      "Up to 2 competitor analysis reports monthly",
      "Platform-specific insights",
      "Replaces $7K+/mo in agency costs",
      "Up to 50 AI Q&A queries/month",
    ],
  },
  {
    name: "Growth",
    price: "$99.99",
    highlighted: true,
    features: [
      "All platforms",
      "50$-150k+ monthly ad spend",
      "Priority customer support",
      "Up to 10 projects created",
      "Up to 10 ICPs generated monthly",
      "Up to 25 ad strategies generated monthly",
      "Up to 10 campaign insights reports monthly",
      "Up to 6 competitor analysis reports monthly",
      "Cross-platform insights",
      "Replaces $12K+/mo in agency costs",
      "Up to 200 AI Q&A queries/month",
    ],
  },
  {
    name: "Pro",
    price: "$999.99",
    features: [
      "All platforms",
      "$150k-$300k monthly ad spend",
      "Priority customer support",
      "Up to 25 projects created",
      "Up to 50 ICPs generated monthly",
      "Up to 50 ad strategies generated monthly",
      "Up to 25 campaign insights reports monthly",
      "Up to 12 competitor analysis reports monthly",
      "Cross-platform insights",
      "Replaces $18K+/mo in agency costs",
      "Up to 500 AI Q&A queries/month",
    ],
  },
  {
    name: "Scale",
    price: "$4,999.99",
    features: [
      "All platforms",
      "Unlimited ad spend",
      "Dedicated success manager",
      "Unlimited projects created",
      "Up to 100 ICPs generated monthly",
      "Up to 100 ad strategies generated monthly",
      "Unlimited campaign insights reports",
      "Unlimited competitor analysis reports",
      "Cross-platform insights",
      "Replaces $22K+/mo in agency costs",
      "Unlimited AI Q&A queries",
    ],
  },
];

const ModelSelectionCard = ({ selectedModel, onModelChange, onSave }: any) => {
  const [hasChanges, setHasChanges] = useState(false);

  const handleModelChange = (event: Event | any) => {
    onModelChange(event.target.value);
    setHasChanges(true);
  };

  return (
    <StyledCard variants={itemVariants}>
      <Box sx={{ p: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Brain style={{ marginRight: 12, color: "#EF4444" }} />
              <Typography variant="h5" fontWeight="bold">
                AI Model Selection
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Choose the AI model that powers your marketing insights
            </Typography>
          </Box>
          {hasChanges && (
            <StyledButton
              variant="contained"
              onClick={() => {
                onSave();
                setHasChanges(false);
              }}
              endIcon={<ArrowRight size={16} />}
            >
              Save Changes
            </StyledButton>
          )}
        </Box>

        <FormControl component="fieldset" sx={{ width: "100%" }}>
          <RadioGroup value={selectedModel} onChange={handleModelChange}>
            {modelOptions.map((model) => {
              const Icon = model.icon;
              return (
                <Paper
                  key={model.id}
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 2,
                    border: "1px solid",
                    borderColor:
                      selectedModel === model.id ? "error.main" : "grey.200",
                    borderRadius: 3,
                    backgroundColor:
                      selectedModel === model.id
                        ? "rgba(239, 68, 68, 0.05)"
                        : "white",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor:
                        selectedModel === model.id ? "error.main" : "grey.300",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                    },
                  }}
                >
                  <FormControlLabel
                    value={model.id}
                    control={
                      <Radio
                        sx={{
                          "&.Mui-checked": {
                            color: "#EF4444",
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Icon
                            size={18}
                            style={{
                              color:
                                selectedModel === model.id
                                  ? "#EF4444"
                                  : "#4B5563",
                            }}
                          />
                          <Typography
                            fontWeight="bold"
                            sx={{
                              color:
                                selectedModel === model.id
                                  ? "#EF4444"
                                  : "inherit",
                            }}
                          >
                            {model.name}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, ml: 0.5 }}
                        >
                          {model.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start" }}
                  />
                </Paper>
              );
            })}
          </RadioGroup>
        </FormControl>
      </Box>
    </StyledCard>
  );
};

const SubscriptionCard = ({ currentPlan, onPlanChange }: any) => {
  const [selectedPlan, setSelectedPlan] = useState(currentPlan);
  const [showPlans, setShowPlans] = useState(false);

  const handlePlanSelect = (
    event: React.MouseEvent,
    planName: string | any
  ) => {
    event.preventDefault();
    setSelectedPlan(planName);
    onPlanChange(planName);
  };

  const planLabel = useMemo(() => capitalize(currentPlan), [currentPlan]);
  const selectedPlanLabel = useMemo(
    () => capitalize(selectedPlan),
    [selectedPlan]
  );

  return (
    <StyledCard variants={itemVariants}>
      <Box className="p-4">
        <Box className="flex flex-col md:flex-row justify-between items-start mb-3">
          <Box>
            <Box className="flex items-center mb-1">
              <CreditCard style={{ marginRight: 12, color: "#EF4444" }} />
              <Typography variant="h5" fontWeight="bold" className="font-bold">
                Billing & Subscription
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" className="mb-2">
              Manage your subscription and billing information
            </Typography>
          </Box>
          <Box className="flex flex-col md:flex-row gap-2 mt-2 md:mt-0">
            <StyledButton
              variant="outlined"
              onClick={() =>
                (window.location.href = config.links.stripeCustomerPortalURL)
              }
              className="w-full md:w-auto"
            >
              Billing Portal
            </StyledButton>
            <StyledButton
              variant="contained"
              onClick={() => setShowPlans(!showPlans)}
              endIcon={showPlans ? null : <ArrowRight size={16} />}
              className="w-full md:w-auto"
            >
              {showPlans ? "Hide Plans" : "View Plans"}
            </StyledButton>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: "rgba(239, 68, 68, 0.05)",
            borderRadius: 3,
            p: 3,
            border: "1px solid",
            borderColor: "rgba(239, 68, 68, 0.2)",
            mb: showPlans ? 4 : 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography fontWeight="bold" fontSize="1.1rem">
                Current Plan: {planLabel}
              </Typography>
            </Box>
            <Chip
              label="Active"
              size="small"
              sx={{
                bgcolor: "#FEE2E2",
                color: "#EF4444",
                borderColor: "#FECACA",
                fontWeight: "bold",
                px: 1,
              }}
            />
          </Box>
        </Box>

        {showPlans && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Available Plans
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr 1fr",
                },
                gap: 3,
                mt: 2,
              }}
            >
              {pricingPlans.map((plan) => (
                <Paper
                  key={plan.name}
                  elevation={0}
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor:
                      selectedPlanLabel === plan.name ? "#EF4444" : "grey.200",
                    borderRadius: 3,
                    position: "relative",
                    backgroundColor:
                      selectedPlanLabel === plan.name
                        ? "rgba(239, 68, 68, 0.05)"
                        : "white",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
                    },
                  }}
                >
                  {plan.highlighted && (
                    <Chip
                      label="Most Popular"
                      color="error"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: 20,
                        fontWeight: "bold",
                      }}
                    />
                  )}
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                      color:
                        selectedPlanLabel === plan.name ? "#EF4444" : "inherit",
                    }}
                  >
                    {plan.name}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                      color:
                        selectedPlanLabel === plan.name ? "#EF4444" : "inherit",
                    }}
                  >
                    {plan.price}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      /month
                    </Typography>
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <List dense>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Check
                            size={16}
                            style={{
                              color:
                                selectedPlanLabel === plan.name
                                  ? "#EF4444"
                                  : "#10B981",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: index < 3 ? "medium" : "normal",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <StyledButton
                    variant={
                      selectedPlanLabel === plan.name ? "outlined" : "contained"
                    }
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={(e: any) => handlePlanSelect(e, plan.name)}
                    endIcon={
                      selectedPlanLabel !== plan.name ? (
                        <ArrowRight size={16} />
                      ) : null
                    }
                  >
                    {selectedPlanLabel === plan.name
                      ? "Current Plan"
                      : "Select Plan"}
                  </StyledButton>
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </StyledCard>
  );
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [selectedModel, setSelectedModel] = useState("openai");
  const [currentPlan, setCurrentPlan] = useState("starter");
  const [settings, setSettings] = useState<any>({
    notifications: {
      emails: true,
    },
  });
  const [userId, setUserId] = useState<string | any>("");

  const handleInitialLoad = useCallback(async () => {
    try {
      let sessionData: any;
      try {
        const sessionService = new SessionService();
        sessionData = await sessionService.reviewSession();
        if (!sessionData.success) {
          navigate(homeRoutePath, { replace: true });
          return;
        }
      } catch (sessionError: any) {
        logIssueNetworkRequestError(
          "Settings.reviewSession error",
          sessionError
        );
        navigate(homeRoutePath, { replace: true });
        return;
      } finally {
        dispatch(setUser(sessionData.userSessionData));
        setUserId(sessionData.userSessionData.id);

        // Fetch settings
        const settingsService = new SettingsService();
        const settingsData = await settingsService.getSettings(
          sessionData.userSessionData.id
        );

        // Fetch subscription data
        const subscriptionService = new SubscriptionService();
        const subscriptionData = await subscriptionService.getSubscription(
          sessionData.userSessionData.id
        );

        // Update settings and subscription state
        setSettings({
          notifications: {
            emails: settingsData.emailNotifications,
            push: settingsData.pushNotifications,
            sms: settingsData.smsNotifications,
          },
          modelSelection: settingsData.modelSelection,
        });
        setSelectedModel(settingsData.modelSelection || "openai");

        // Set current plan from subscription data if available, otherwise fallback to settings
        if (subscriptionData?.plan?.name) {
          const planName = subscriptionData.plan.name.replace("Plan", "");
          setCurrentPlan(planName);
        } else {
          setCurrentPlan(settingsData.currentPlan || "growth");
        }
      }
    } catch (error: any) {
      logIssueNetworkRequestError("SettingsPage.handleInitialLoad", error);
      setError("Failed to load settings. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  const handleModelSave = async () => {
    try {
      const settingsService = new SettingsService();
      await settingsService.updateNotifications({
        userId,
        modelSelection: selectedModel,
        emailNotifications: settings.notifications.emails,
        pushNotifications: settings.notifications.push,
        smsNotifications: settings.notifications.sms,
      });
      displayToastMessage("success", "Model selection saved successfully");
    } catch (error: any) {
      console.error("Error saving model selection:", error);
      setError("Failed to save model selection. Please try again.");
    }
  };

  const handlePlanChange = useCallback(
    async (planName: string) => {
      try {
        setLoading(true);
        const subscriptionService = new SubscriptionService();

        // Convert plan name to the subscription plan ID format
        const subscriptionPlan = `${planName.toLowerCase()}Plan`;

        // Call the subscription service
        const response = await subscriptionService.joinOrUpdateSubscription({
          userId,
          subscriptionPlan,
        });

        // Handle different response types
        if (response.sessionURL) {
          // New subscription - redirect to Stripe Checkout
          window.location.href = response.sessionURL;
        } else if (response.success) {
          // Existing subscription was updated
          setCurrentPlan(planName.toLowerCase());
          // Show success message to user
          setError("");
          displayToastMessage("success", `Subscribed to new plan: ${planName}`);
        } else {
          throw new Error(
            "Unexpected response format from subscription service"
          );
        }
      } catch (error: any) {
        console.error("Error changing plan:", error);
        setError("Failed to change plan. Please try again.");
        setCurrentPlan(currentPlan); // Reset to previous plan on error
      } finally {
        setLoading(false);
      }
    },
    [userId, currentPlan, setLoading, setError, setCurrentPlan]
  );

  const toggleNotification = async (key: string) => {
    try {
      const newSettings = {
        ...settings,
        notifications: {
          ...settings.notifications,
          [key]: !settings.notifications[key],
        },
      };

      const settingsService = new SettingsService();
      await settingsService.updateNotifications({
        userId,
        emailNotifications: newSettings.notifications.emails,
        pushNotifications: newSettings.notifications.push,
        smsNotifications: newSettings.notifications.sms,
        modelSelection: selectedModel,
      });

      setSettings(newSettings);
      displayToastMessage(
        "success",
        "Notification preferences updated successfully"
      );
    } catch (error: any) {
      console.error("Error updating notifications:", error);
      setError("Failed to update notification preferences. Please try again.");
    }
  };

  const requestAccountDeletion = async () => {
    if (deleteConfirmation.toLowerCase() !== "delete my account") {
      (window as any).alert("Please type 'delete my account' completely.");
      return;
    }

    try {
      const settingsService = new SettingsService();
      await settingsService.requestAccountDeletion({
        userId,
        confirmDeletion: true,
      });

      setShowDeleteDialog(false);

      const sessionService = new SessionService();

      await sessionService.signOut();

      displayToastMessage("success", "Account deletion requested successfully");

      navigate(homeRoutePath, { replace: true, state: { session: false } });
    } catch (error: any) {
      console.error("Error deleting account:", error);
      setError("Failed to delete account. Please try again.");
    }
  };

  const handleSignOut = useCallback(async (event: React.MouseEvent) => {
    try {
      event.preventDefault();

      const sessionService = new SessionService();

      await sessionService.signOut();

      navigate(homeRoutePath, { replace: true, state: { session: false } });
    } catch (error: any) {
      console.error("Error signing out of account:", error);
      setError("Failed to sign out. Please try again.");
    }
  }, []);

  if (loading) {
    return (
      <CommonLayout>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-gray-200 rounded-lg w-1/4 mb-8"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </Container>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            gutterBottom
            sx={{
              mb: 4,
              color: "#111827",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Settings
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 4,
                borderRadius: 3,
                "& .MuiAlert-icon": {
                  color: "#EF4444",
                },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Model Selection Section */}
          <ModelSelectionCard
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            onSave={handleModelSave}
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Subscription Section */}
            <SubscriptionCard
              currentPlan={currentPlan}
              onPlanChange={handlePlanChange}
            />

            {/* Notifications Section */}
            <StyledCard variants={itemVariants}>
              <Box sx={{ p: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <BellRing style={{ marginRight: 12, color: "#EF4444" }} />
                  <Typography variant="h5" fontWeight="bold">
                    Notification Preferences
                  </Typography>
                </Box>

                {Object.entries(settings.notifications).map(
                  ([key, enabled]: any) => (
                    <Box
                      key={key}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 3,
                        px: 4,
                        borderRadius: 3,
                        backgroundColor: "rgba(239, 68, 68, 0.05)",
                        border: "1px solid",
                        borderColor: "rgba(239, 68, 68, 0.1)",
                        mb: 2,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                          borderColor: "rgba(239, 68, 68, 0.2)",
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          fontWeight="bold"
                          sx={{
                            textTransform: "capitalize",
                            mb: 0.5,
                            fontSize: "1.05rem",
                          }}
                        >
                          {capitalize(key)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getNotificationDescription(key)}
                        </Typography>
                      </Box>
                      <StyledSwitch
                        checked={enabled}
                        onChange={() => toggleNotification(key)}
                      />
                    </Box>
                  )
                )}
              </Box>
            </StyledCard>

            {/* Account Section */}
            <StyledCard variants={itemVariants}>
              <Box sx={{ p: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Lock style={{ marginRight: 12, color: "#EF4444" }} />
                  <Typography variant="h5" fontWeight="bold">
                    Account
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box
                    sx={{
                      bgcolor: "rgba(239, 68, 68, 0.05)",
                      borderRadius: 3,
                      p: 3,
                      border: "1px solid",
                      borderColor: "rgba(239, 68, 68, 0.1)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                        borderColor: "rgba(239, 68, 68, 0.2)",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        fontWeight="bold"
                        fontSize="1.05rem"
                        sx={{ mb: 0.5 }}
                      >
                        Log out
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        End all active sessions
                      </Typography>
                    </Box>
                    <StyledButton
                      variant="outlined"
                      startIcon={<LogOut />}
                      onClick={(e) => handleSignOut(e)}
                    >
                      Log Out
                    </StyledButton>
                  </Box>

                  <Box
                    sx={{
                      bgcolor: "#FEF2F2",
                      borderRadius: 3,
                      p: 3,
                      border: "1px solid",
                      borderColor: "#FECACA",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(253, 232, 232, 0.7)",
                        borderColor: "#EF4444",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        fontWeight="bold"
                        color="#EF4444"
                        fontSize="1.05rem"
                        sx={{ mb: 0.5 }}
                      >
                        Delete account
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Permanently delete your account and all data
                      </Typography>
                    </Box>
                    <StyledButton
                      variant="outlined"
                      color="error"
                      startIcon={<Trash2 />}
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      Delete Account
                    </StyledButton>
                  </Box>
                </Box>
              </Box>
            </StyledCard>
          </Box>

          {/* Delete Account Dialog */}
          <Dialog
            open={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              style: {
                borderRadius: "16px",
                padding: "8px",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#EF4444",
                fontWeight: "bold",
                fontSize: "1.5rem",
                pt: 3,
                pb: 2,
              }}
            >
              <AlertCircle style={{ marginRight: 12 }} />
              Delete Account
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 3, fontSize: "1rem" }}>
                This action cannot be undone. This will permanently delete your
                account and remove all data from our servers.
              </DialogContentText>

              <Alert
                severity="error"
                sx={{
                  my: 3,
                  py: 2,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    color: "#EF4444",
                  },
                  "& .MuiAlert-message": {
                    fontWeight: "medium",
                  },
                }}
              >
                Type "delete my account" to confirm deletion
              </Alert>

              <TextField
                fullWidth
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="delete my account"
                variant="outlined"
                sx={{
                  mt: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&.Mui-focused fieldset": {
                      borderColor: "#EF4444",
                      borderWidth: "2px",
                    },
                  },
                }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={() => setShowDeleteDialog(false)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  textTransform: "none",
                  fontWeight: "medium",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#EF4444",
                  "&:hover": {
                    bgcolor: "#DC2626",
                  },
                  borderRadius: 2,
                  boxShadow: "0 4px 14px rgba(239, 68, 68, 0.3)",
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: "bold",
                }}
                onClick={requestAccountDeletion}
                disabled={
                  deleteConfirmation.toLowerCase() !== "delete my account"
                }
              >
                Delete Account
              </Button>
            </DialogActions>
          </Dialog>
        </motion.div>
      </Container>
    </CommonLayout>
  );
};

// Helper function for notification descriptions
const getNotificationDescription = (key: any) => {
  const descriptions = {
    emails:
      "Receive email notifications of your projects, company updates, and more",
    push: "Receive push notifications on your device for important updates",
    sms: "Receive SMS notifications for critical alerts and reminders",
  } as any;
  return descriptions[key] || "";
};

export default SettingsPage;
